import { buildTranslate } from 'locales'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { calculateEstimatedMaxValue } from 'scenes/RedeemManager/helpers'
import {
  FormModel,
  MultiUseRewardsSettings,
  useFormModel,
} from '../../FormModel'
import {
  detectPreset,
  PresetConfig,
  PresetKey,
  PRESETS,
} from './multi_use_presets'

function formatDuration(days: number): string {
  return days === 1 ? '1 day' : `${days} days`
}

const t = buildTranslate(
  'redeem_manager.builder.steps.configuration.multi_use_rewards_card'
)

interface UseMultiUseRewardsStateParams {
  settings: MultiUseRewardsSettings
  discount?: string | null
  onFormChange: (newFormModel: FormModel, isChanged: boolean) => void
}

export function useMultiUseRewardsState({
  settings,
  discount,
  onFormChange,
}: UseMultiUseRewardsStateParams) {
  const { model } = useFormModel()
  const modelRef = useRef(model)
  modelRef.current = model

  const [showCustomForm, setShowCustomForm] = useState(false)
  const [selectedPreset, setSelectedPreset] = useState<PresetKey | null>(null)
  const prevUsageTypeRef = useRef(settings.usageType)
  const customConfigRef = useRef<PresetConfig | null>(null)

  const { usageType } = settings

  // Detect preset on mount for existing configurations.
  // Captures the initial settings snapshot via ref so this only runs once
  // without needing settings in the dependency array.
  const initialSettingsRef = useRef(settings)
  const didMountRef = useRef(false)
  useEffect(() => {
    if (didMountRef.current) return
    didMountRef.current = true

    const initial = initialSettingsRef.current
    if (initial.usageType !== 'multiple_times') return

    const detected = detectPreset(initial)
    if (detected === 'custom') {
      setSelectedPreset('custom')
      setShowCustomForm(true)
      // Save initial custom config
      customConfigRef.current = {
        limitType: initial.limitType || 'set_number',
        maxIssuancesPerWindow: initial.maxIssuancesPerWindow,
        windowDuration: initial.windowDuration,
        maxIssuances: initial.maxIssuances,
        activeWindowDuration: initial.activeWindowDuration || 0,
      }
    } else if (detected) {
      setSelectedPreset(detected)
    }
  }, [])

  // Track custom config whenever the user edits settings in the custom form.
  // Skip saves triggered by entering custom mode (showCustomForm/selectedPreset
  // changing) — only save when actual setting values change.
  const isInCustomForm = showCustomForm && selectedPreset === 'custom'
  const wasInCustomFormRef = useRef(isInCustomForm)
  useEffect(() => {
    if (!isInCustomForm) {
      wasInCustomFormRef.current = false
      return
    }
    // Skip the first run after entering custom form
    if (!wasInCustomFormRef.current) {
      wasInCustomFormRef.current = true
      return
    }
    customConfigRef.current = {
      limitType: settings.limitType || 'set_number',
      maxIssuancesPerWindow: settings.maxIssuancesPerWindow,
      windowDuration: settings.windowDuration,
      maxIssuances: settings.maxIssuances,
      activeWindowDuration: settings.activeWindowDuration || 0,
    }
  }, [
    isInCustomForm,
    settings.limitType,
    settings.maxIssuancesPerWindow,
    settings.windowDuration,
    settings.maxIssuances,
    settings.activeWindowDuration,
  ])

  // Reset to daily preset when switching to multiple_times
  if (
    prevUsageTypeRef.current !== 'multiple_times' &&
    usageType === 'multiple_times'
  ) {
    setSelectedPreset('daily')
    setShowCustomForm(false)
  }
  // Clear preset when switching away from multiple_times
  if (
    prevUsageTypeRef.current === 'multiple_times' &&
    usageType !== 'multiple_times'
  ) {
    setSelectedPreset(null)
    setShowCustomForm(false)
  }
  prevUsageTypeRef.current = usageType

  // When a named preset is selected (not custom), use the preset config
  // as the source of truth. This avoids race conditions with Formsy's
  // async onChange overwriting values during radio/preset transitions.
  const presetConfig =
    selectedPreset && selectedPreset !== 'custom'
      ? PRESETS.find(p => p.key === selectedPreset)?.config
      : null

  const limitType = showCustomForm
    ? settings.limitType || 'set_number'
    : presetConfig?.limitType || settings.limitType || 'set_number'
  const maxIssuancesPerWindow = showCustomForm
    ? settings.maxIssuancesPerWindow
    : presetConfig?.maxIssuancesPerWindow ?? settings.maxIssuancesPerWindow
  const windowDuration = showCustomForm
    ? settings.windowDuration
    : presetConfig?.windowDuration ?? settings.windowDuration
  const maxIssuances = showCustomForm
    ? settings.maxIssuances
    : presetConfig !== null
    ? presetConfig?.maxIssuances ?? undefined
    : settings.maxIssuances
  const activeWindowDuration = showCustomForm
    ? settings.activeWindowDuration
    : presetConfig?.activeWindowDuration ?? settings.activeWindowDuration

  const isUnlimited = limitType === 'unlimited'
  const showMaxIssuances = isUnlimited || maxIssuances != null

  const estimatedMaxValue = useMemo(() => {
    if (!discount) return undefined
    // Use derived values (from preset config or settings) so the estimate
    // stays consistent with what is displayed, avoiding stale values when
    // Formsy hasn't flushed preset changes to settings yet.
    return calculateEstimatedMaxValue(
      {
        maxIssuances,
        maxIssuancesPerWindow,
        activeWindowDuration,
        windowDuration,
      } as MultiUseRewardsSettings,
      discount
    )
  }, [
    maxIssuances,
    activeWindowDuration,
    maxIssuancesPerWindow,
    windowDuration,
    discount,
  ])

  const updateAndSave = useCallback(
    (updatedModel: FormModel) => {
      // Only call onFormChange — it already calls setFormModel internally
      // (which is the same setter as setModel). Calling both would cause
      // double state updates and trigger Formsy's onChange cascade.
      onFormChange(updatedModel, true)
    },
    [onFormChange]
  )

  const applyPreset = useCallback(
    (preset: PresetKey) => {
      setSelectedPreset(preset)

      if (preset === 'custom') {
        setShowCustomForm(true)
        // Restore saved custom config, or use current preset values as starting point
        const config =
          customConfigRef.current ||
          (presetConfig ?? PRESETS.find(p => p.key === 'daily')?.config)
        if (config) {
          updateAndSave({
            ...modelRef.current,
            multiUseRewards: {
              ...modelRef.current.multiUseRewards,
              usageType: 'multiple_times' as const,
              limitType: config.limitType,
              maxIssuancesPerWindow: config.maxIssuancesPerWindow,
              windowDuration: config.windowDuration,
              maxIssuances: config.maxIssuances ?? undefined,
              activeWindowDuration: config.activeWindowDuration,
            },
          })
        }
        return
      }

      setShowCustomForm(false)
      const config = PRESETS.find(p => p.key === preset)?.config
      if (!config) return

      updateAndSave({
        ...modelRef.current,
        multiUseRewards: {
          ...modelRef.current.multiUseRewards,
          usageType: 'multiple_times' as const,
          limitType: config.limitType,
          maxIssuancesPerWindow: config.maxIssuancesPerWindow,
          windowDuration: config.windowDuration,
          maxIssuances: config.maxIssuances ?? undefined,
          activeWindowDuration: config.activeWindowDuration,
        },
      })
    },
    [updateAndSave, presetConfig]
  )

  const toggleMaxIssuances = () => {
    updateAndSave({
      ...modelRef.current,
      multiUseRewards: {
        ...modelRef.current.multiUseRewards,
        maxIssuances: maxIssuances != null ? undefined : 2,
      },
    })
  }

  const handleCustomizeClick = () => {
    // When entering custom form from a preset, apply the preset values
    // to the form model so FormsyInputs pick them up
    if (presetConfig) {
      updateAndSave({
        ...modelRef.current,
        multiUseRewards: {
          ...modelRef.current.multiUseRewards,
          usageType: 'multiple_times' as const,
          limitType: presetConfig.limitType,
          maxIssuancesPerWindow: presetConfig.maxIssuancesPerWindow,
          windowDuration: presetConfig.windowDuration,
          maxIssuances: presetConfig.maxIssuances ?? undefined,
          activeWindowDuration: presetConfig.activeWindowDuration,
        },
      })
    }
    setSelectedPreset('custom')
    setShowCustomForm(true)
  }

  const handleBackToPresets = () => {
    setShowCustomForm(false)
    setSelectedPreset(detectPreset(settings))
  }

  const handleLimitTypeChange = (newLimitType: 'set_number' | 'unlimited') => {
    updateAndSave({
      ...modelRef.current,
      multiUseRewards: {
        ...modelRef.current.multiUseRewards,
        limitType: newLimitType,
        ...(newLimitType === 'unlimited'
          ? {
              maxIssuancesPerWindow: undefined,
              windowDuration: undefined,
              ...(!modelRef.current.multiUseRewards.maxIssuances
                ? { maxIssuances: 2 }
                : {}),
            }
          : {
              maxIssuancesPerWindow: 1,
              windowDuration: 1,
            }),
      },
    })
  }

  const summaryText = useMemo(() => {
    if (!maxIssuancesPerWindow || !windowDuration) return null

    const hours = windowDuration * 24
    const hourLabel = hours === 1 ? t('summary_hour') : t('summary_hours')
    const frequencyPart = t('summary_frequency', {
      count: maxIssuancesPerWindow,
      hours,
      hourLabel,
    })

    let maxPart = ''
    if (maxIssuances && activeWindowDuration) {
      maxPart = ` ${t('summary_max', {
        count: maxIssuances,
        duration: formatDuration(activeWindowDuration),
      })}`
    } else if (activeWindowDuration) {
      maxPart = ` ${t('summary_over', {
        duration: formatDuration(activeWindowDuration),
      })}`
    }

    return { frequencyPart, maxPart }
  }, [
    maxIssuancesPerWindow,
    windowDuration,
    maxIssuances,
    activeWindowDuration,
  ])

  const customSummaryText = useMemo(() => {
    if (!isUnlimited || !activeWindowDuration || !maxIssuances) return null
    const duration = formatDuration(activeWindowDuration)
    return t('reissue_summary', { count: maxIssuances, duration })
  }, [activeWindowDuration, isUnlimited, maxIssuances])

  const isNextDisabled =
    usageType === 'multiple_times' &&
    (!activeWindowDuration ||
      (limitType === 'unlimited'
        ? !maxIssuances
        : !maxIssuancesPerWindow || !windowDuration))

  return {
    showCustomForm,
    selectedPreset,
    showMaxIssuances,
    usageType,
    limitType,
    maxIssuances,
    activeWindowDuration,
    maxIssuancesPerWindow,
    windowDuration,
    isUnlimited,
    isNextDisabled,
    estimatedMaxValue,
    summaryText,
    customSummaryText,
    applyPreset,
    toggleMaxIssuances,
    handleCustomizeClick,
    handleBackToPresets,
    handleLimitTypeChange,
  }
}
